import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { schedulingApi } from "../../services/schedulingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { usePermissions } from "../../hooks/usePermissions";
import { StoreSelect } from "../../components/StoreSelect";
import { useLocalization } from "../../hooks/useLocalization";

const EMPTY_FORM = { store_location: null, period_start: "", period_end: "", notes: "" };

export default function ScheduleListPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canEditSchedule, canViewScheduling } = usePermissions();
  const [schedules, setSchedules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const load = () => schedulingApi.listSchedules().then((r) => setSchedules(r.data)).catch(() => {});

  useEffect(() => {
    if (!canViewScheduling) return;
    load();
  }, [canViewScheduling]);

  if (!canViewScheduling) return <p className="text-sm text-gray-500" data-testid="schedule-list-access-denied">{t("common.accessDenied")}</p>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.store_location) { setError(t("mgmtScheduling.selectStoreError")); return; }
    try {
      const { data } = await schedulingApi.createSchedule(form);
      toast.success(t("mgmtScheduling.draftCreated"));
      navigate(`/management/scheduling/schedules/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="schedule-list-page">
      <PageHelpBanner pageKey="schedule-list" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtScheduling.listHelp") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtScheduling.listTitle")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtScheduling.listSubtitle")}</p>
        </div>
        {canEditSchedule && (
          <button data-testid="new-schedule-button" onClick={() => setShowForm((s) => !s)} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
            <Plus size={15} /> {t("mgmtScheduling.newSchedule")}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="new-schedule-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StoreSelect testId="new-schedule-store-input" value={form.store_location} onChange={(v) => setForm((f) => ({ ...f, store_location: v }))} className="px-3 py-2" />
            <input data-testid="new-schedule-period-start-input" required type="date" value={form.period_start} onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="new-schedule-period-end-input" required type="date" value={form.period_end} onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          {error && <p className="text-sm text-red-500" data-testid="new-schedule-error">{error}</p>}
          <button data-testid="submit-new-schedule-button" type="submit" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">{t("mgmtScheduling.createDraft")}</button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colStore")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colPeriod")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtPayroll.colVersion")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`schedule-row-${s.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/management/scheduling/schedules/${s.id}`} className="text-blue-600 font-semibold hover:underline" data-testid={`schedule-link-${s.id}`}>
                    {s.store_location}
                  </Link>
                </td>
                <td className="px-4 py-3">{s.period_start} {t("mgmtScheduling.to")} {s.period_end}</td>
                <td className="px-4 py-3">v{s.version}</td>
                <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
              </tr>
            ))}
            {schedules.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">{t("mgmtScheduling.noSchedules")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
